/**
 * schedulerApi.js — vendor-spec-aligned client for `/api/scheduler/…`.
 *
 * Single source of truth for every scheduler network call. All paths here
 * match the AriaOne API Reference (§15–§20) and the vendor integration
 * contract documented in `/app/docs/SCHEDULER_API_AUDIT.md`.
 *
 * Guarantees enforced here (tested by `yarn mode-separation`):
 *
 *   1. Canonical vendor paths only — NO legacy aliases
 *      (no `/availability`, `/availability/range`, `/appointments/check-duplicate`,
 *      `/ai/daily-summary`, `/visit-types/ai-suggest` — all moved under
 *      `/slots/available`, `/slots/calendar`, `/check-duplicate`,
 *      `/daily-summary`, `/smart-visit-types`).
 *
 *   2. PATCH for status mutation — `cancelAppointment` and
 *      `updateAppointmentStatus` both use PATCH (vendor §18).
 *
 *   3. Vendor §17 required scope — `queryAvailability` rejects with
 *      `MISSING_SCOPE` BEFORE the network call when either `entity_id` or
 *      `location_id` is absent. Prevents the 422 regression where the
 *      vendor returned `{detail:[{type:"missing", loc:["query","entity_id"]}]}`.
 *
 *   4. Same-origin internal helpers — non-vendor scheduler paths (overrides,
 *      policies, front-desk checkin/exam/checkout) pin `{ vendor: false }`
 *      so a vendor-mode deployment does not accidentally route these to
 *      the vendor edge (which 403/404s them).
 *
 *   5. Vendor-mode short-circuit — in vendor mode the internal helpers
 *      return a `NOT_SUPPORTED_IN_VENDOR_MODE` error shape without touching
 *      the network (prevents the login-host 403 storm when the app is
 *      served from `login.ariaone.ai` / `crm.ariaone.ai`).
 *
 *   6. Config-scoped writes — every multi-tenant write is scoped under
 *      `/configs/${configId}/…` (schedule, slot-settings, overrides, blocks).
 *
 * Do NOT bypass this wrapper. Callers that hit `fetch()` directly skip
 * every guard above and WILL cause vendor 422s or CORS failures in prod.
 */
import { get, post, put, patch, del, requireParam, VENDOR_MODE_ENABLED } from './httpClient';

const BASE = '/api/scheduler';

/* ═══════════════════════════════════════════════════════════════════════════
   Internal helpers
   ═══════════════════════════════════════════════════════════════════════════ */

/**
 * Returns a rejection with the `NOT_SUPPORTED_IN_VENDOR_MODE` shape that the
 * vendor-mode settings test + the Echo Settings UI both check for.  Used by
 * every internal-only helper to short-circuit before the network call.
 */
const notSupportedInVendorMode = (fn) => ({
  ok: false,
  error: {
    status: 0,
    code: 'NOT_SUPPORTED_IN_VENDOR_MODE',
    message: `${fn} is only supported against the internal backend; not available in vendor mode.`,
    details: { not_supported: true, fn },
  },
});

/* ═══════════════════════════════════════════════════════════════════════════
   §15 — ENTITIES  (Providers, Rooms, Resources)
   ═══════════════════════════════════════════════════════════════════════════ */

export const listEntities = (params = {}) =>
  get(`${BASE}/entities`, params);

export const getEntity = (entityId) => {
  requireParam('entityId', entityId);
  return get(`${BASE}/entities/${entityId}`);
};

export const createEntity = (entity) => {
  requireParam('entity.name', entity?.name);
  requireParam('entity.kind', entity?.kind);
  return post(`${BASE}/entities`, entity);
};

export const updateEntity = (entityId, updates) => {
  requireParam('entityId', entityId);
  return put(`${BASE}/entities/${entityId}`, updates);
};

export const deleteEntity = (entityId) => {
  requireParam('entityId', entityId);
  return del(`${BASE}/entities/${entityId}`);
};

/* ── §15b — Locations (provider-scoped clinic locations) ─────────────────
   Vendor and local backends both expose `/api/scheduler/locations`. The UI
   uses this list to satisfy the vendor §17 both-scope contract (every
   availability call carries entity_id + location_id). */
export const listLocations = (params = {}) =>
  get(`${BASE}/locations`, params);

export const createLocation = (location) => {
  requireParam('location.name', location?.name);
  return post(`${BASE}/locations`, location);
};

export const updateLocation = (locationId, updates) => {
  requireParam('locationId', locationId);
  return put(`${BASE}/locations/${locationId}`, updates);
};

export const deleteLocation = (locationId) => {
  requireParam('locationId', locationId);
  return del(`${BASE}/locations/${locationId}`);
};

/* ═══════════════════════════════════════════════════════════════════════════
   §16 — SCHEDULE CONFIGS
   ═══════════════════════════════════════════════════════════════════════════ */

export const listConfigs = (params = {}) =>
  get(`${BASE}/configs`, params);

export const getConfig = (configId) => {
  requireParam('configId', configId);
  return get(`${BASE}/configs/${configId}`);
};

export const createConfig = (config) => {
  requireParam('config.entity_id', config?.entity_id);
  return post(`${BASE}/configs`, config);
};

export const updateConfig = (configId, updates) => {
  requireParam('configId', configId);
  return put(`${BASE}/configs/${configId}`, updates);
};

export const deleteConfig = (configId) => {
  requireParam('configId', configId);
  return del(`${BASE}/configs/${configId}`);
};

/* ── Config-scoped writes (vendor spec §16) ── */

export const updateConfigSchedule = (configId, body) => {
  requireParam('configId', configId);
  return put(`${BASE}/configs/${configId}/schedule`, body);
};

export const updateConfigSlotSettings = (configId, body) => {
  requireParam('configId', configId);
  return put(`${BASE}/configs/${configId}/slot-settings`, body);
};

export const addConfigOverride = (configId, override) => {
  requireParam('configId', configId);
  return post(`${BASE}/configs/${configId}/overrides`, override);
};

export const updateConfigOverride = (configId, overrideId, updates) => {
  requireParam('configId', configId);
  requireParam('overrideId', overrideId);
  return put(`${BASE}/configs/${configId}/overrides/${overrideId}`, updates);
};

export const deleteConfigOverride = (configId, overrideId) => {
  requireParam('configId', configId);
  requireParam('overrideId', overrideId);
  return del(`${BASE}/configs/${configId}/overrides/${overrideId}`);
};

export const addConfigBlock = (configId, block) => {
  requireParam('configId', configId);
  return post(`${BASE}/configs/${configId}/blocks`, block);
};

export const bulkAddConfigBlocks = (configId, body) => {
  requireParam('configId', configId);
  return post(`${BASE}/configs/${configId}/blocks/bulk`, body);
};

export const deleteConfigBlock = (configId, blockId) => {
  requireParam('configId', configId);
  requireParam('blockId', blockId);
  return del(`${BASE}/configs/${configId}/blocks/${blockId}`);
};

/* ═══════════════════════════════════════════════════════════════════════════
   §17 — SLOTS & AVAILABILITY
   ═══════════════════════════════════════════════════════════════════════════ */

export const generateSlots = (params) => {
  requireParam('params.start_date', params?.start_date);
  requireParam('params.end_date', params?.end_date);
  return post(`${BASE}/slots/generate`, params);
};

export const listSlots = (params = {}) =>
  get(`${BASE}/slots`, params);

/**
 * Query available slots — vendor §17.
 *
 * REQUIRED: `date` (throws via `requireParam`), AND *both* `entity_id`
 * and `location_id` must be supplied. The vendor refuses partial-scope
 * requests with a 422; we short-circuit locally with a `MISSING_SCOPE`
 * error so the UI can surface a clear "Select a provider + location"
 * prompt instead of bouncing a vendor 422 into the user's face.
 */
export const queryAvailability = (params) => {
  requireParam('params.date', params?.date);
  const missing = [];
  if (!params?.entity_id || !params?.location_id) {
    if (!params?.entity_id) missing.push('entity_id');
    if (!params?.location_id) missing.push('location_id');
    return Promise.resolve({
      ok: false,
      error: {
        status: 0,
        code: 'MISSING_SCOPE',
        message: 'Scheduler availability requires both entity_id and location_id (vendor §17).',
        details: { missing },
      },
    });
  }
  return get(`${BASE}/slots/available`, params);
};

/**
 * Fan-out availability across every location for a given entity/date.
 * Backs the "All locations" discriminator in EchoScheduler — when the
 * user has no single location selected we call this instead of the
 * per-(entity,location) single-scope `queryAvailability`.
 */
export const queryAvailabilityByLocation = (params) => {
  requireParam('params.date', params?.date);
  requireParam('params.entity_id', params?.entity_id);
  return get(`${BASE}/slots/available/by-location`, params);
};

/**
 * Multi-day calendar — vendor §17. Used for week/month views.
 */
export const queryAvailabilityCalendar = (params) => {
  requireParam('params.start_date', params?.start_date);
  requireParam('params.end_date', params?.end_date);
  return get(`${BASE}/slots/calendar`, params);
};

/* ═══════════════════════════════════════════════════════════════════════════
   §18 — APPOINTMENTS
   ═══════════════════════════════════════════════════════════════════════════ */

export const listAppointments = (params = {}) =>
  get(`${BASE}/appointments`, params);

export const getAppointment = (appointmentId) => {
  requireParam('appointmentId', appointmentId);
  return get(`${BASE}/appointments/${appointmentId}`);
};

export const createAppointment = (appointment) => {
  requireParam('appointment.patient_id', appointment?.patient_id);
  requireParam('appointment.entity_id', appointment?.entity_id);
  // Vendor §18 (2026-05-04) additively accepts `{date, time}` in addition
  // to the legacy `start` ISO. Require at least one of the two shapes.
  const hasSplit = !!(appointment?.date && appointment?.time);
  const hasIso = !!(appointment?.start || appointment?.start_datetime);
  if (!hasSplit && !hasIso) {
    requireParam('appointment.date+time (or appointment.start)', null);
  }
  return post(`${BASE}/appointments`, appointment);
};

export const updateAppointment = (appointmentId, updates) => {
  requireParam('appointmentId', appointmentId);
  return put(`${BASE}/appointments/${appointmentId}`, updates);
};

/**
 * Cancel an appointment — PATCH (vendor §18). NOT a DELETE and NOT a POST
 * to a `/cancel` sub-resource. Vendor treats cancellation as a status
 * transition on the appointment itself.
 */
export const cancelAppointment = (appointmentId, body = {}) => {
  requireParam('appointmentId', appointmentId);
  return patch(`${BASE}/appointments/${appointmentId}/cancel`, body);
};

/**
 * Update appointment status — PATCH (vendor §18). Single canonical verb
 * for every non-cancel status transition (booked → checked_in → in_exam
 * → completed, etc.).
 */
export const updateAppointmentStatus = (appointmentId, body = {}) => {
  requireParam('appointmentId', appointmentId);
  requireParam('body.status', body?.status);
  return patch(`${BASE}/appointments/${appointmentId}/status`, body);
};

/**
 * Check for duplicate/conflicting appointments.
 * Vendor path sits at the scheduler root (NOT under `/appointments/`).
 *
 * Vendor §18 (2026-05-04) query contract:
 *   ?patient_id (required) &date (required) [&entity_id] [&time=HH:MM]
 * When `time` is provided the server also evaluates `slot_collision`.
 * For back-compat with older local backends we still forward `start`
 * when the caller passes it — the server ignores unknown params.
 */
export const checkDuplicate = (params) => {
  requireParam('params.patient_id', params?.patient_id);
  // Vendor now requires `date`; fall back to deriving it from `start`
  // so older call-sites that only had the ISO still work.
  if (!params?.date && !params?.start) {
    requireParam('params.date (or params.start)', null);
  }
  return get(`${BASE}/check-duplicate`, params);
};

/* ── Front-desk status helpers ────────────────────────────────────────────
   These used to hit internal `/api/echo/appointments/.../{checkin,
   move-to-exam, checkout}` routes. Per ECHO_SCHEDULER_RECONCILIATION
   (2026-05-04) they now all funnel through the unified vendor §18
   `PATCH /api/scheduler/appointments/{id}/status` endpoint with the
   explicit target status. The helper names are preserved so existing
   UI call-sites continue to work.  Status transitions enforced by the
   vendor: booked → checked_in → in_exam → ready_checkout → completed. */

export const checkinAppointment = (appointmentId, body = {}) => {
  requireParam('appointmentId', appointmentId);
  return patch(`${BASE}/appointments/${appointmentId}/status`, { ...body, status: 'checked_in' });
};

export const moveToExam = (appointmentId, body = {}) => {
  requireParam('appointmentId', appointmentId);
  return patch(`${BASE}/appointments/${appointmentId}/status`, { ...body, status: 'in_exam' });
};

export const markReadyForCheckout = (appointmentId, body = {}) => {
  requireParam('appointmentId', appointmentId);
  return patch(`${BASE}/appointments/${appointmentId}/status`, { ...body, status: 'ready_checkout' });
};

export const checkoutAppointment = (appointmentId, body = {}) => {
  requireParam('appointmentId', appointmentId);
  return patch(`${BASE}/appointments/${appointmentId}/status`, { ...body, status: 'completed' });
};

export const markNoShow = (appointmentId, body = {}) => {
  requireParam('appointmentId', appointmentId);
  return patch(`${BASE}/appointments/${appointmentId}/status`, { ...body, status: 'no_show' });
};

/* ═══════════════════════════════════════════════════════════════════════════
   §19 — VISIT TYPES & AI
   ═══════════════════════════════════════════════════════════════════════════ */

export const listVisitTypes = (params = {}) =>
  get(`${BASE}/visit-types`, params);

export const createVisitType = (visitType) => {
  requireParam('visitType.name', visitType?.name);
  requireParam('visitType.duration_min', visitType?.duration_min);
  return post(`${BASE}/visit-types`, visitType);
};

export const updateVisitType = (visitTypeId, updates) => {
  requireParam('visitTypeId', visitTypeId);
  return put(`${BASE}/visit-types/${visitTypeId}`, updates);
};

export const deleteVisitType = (visitTypeId) => {
  requireParam('visitTypeId', visitTypeId);
  return del(`${BASE}/visit-types/${visitTypeId}`);
};

/**
 * AI visit-type suggestion — vendor §19. Canonical path is
 * `/smart-visit-types` (NOT the legacy `/visit-types/ai-suggest`).
 */
export const smartVisitTypes = (params) => {
  requireParam('params.patient_id', params?.patient_id);
  return get(`${BASE}/smart-visit-types`, params);
};

/**
 * Daily scheduling summary — vendor §19. Canonical path is `/daily-summary`
 * (NOT the legacy `/ai/daily-summary`).
 */
export const getDailySummary = (params = {}) =>
  get(`${BASE}/daily-summary`, params);

/* ═══════════════════════════════════════════════════════════════════════════
   §20 — AI CONFIGURATION & BOOKING
   ═══════════════════════════════════════════════════════════════════════════ */

/**
 * `/config/ai-assist` POST — vendor-side AI assist request (sent to the
 * vendor edge, can be disabled per-org).
 */
export const aiAssist = (body) =>
  post(`${BASE}/config/ai-assist`, body);

/**
 * `/config/ai-apply` POST — commits an AI-assisted scheduling suggestion.
 */
export const aiApply = (body) => {
  requireParam('body.suggestion_id', body?.suggestion_id);
  return post(`${BASE}/config/ai-apply`, body);
};

/**
 * Natural-language scheduling query.
 */
export const aiBookingQuery = (body) => {
  requireParam('body.query', body?.query);
  return post(`${BASE}/ai/query`, body);
};

/* ── Internal AI-scheduling policy (overbooking, auto-reminders, …) ──────
   Lives at `/policies` on the local backend — NOT the vendor edge. In
   vendor mode these short-circuit so the UI renders a "managed per-config"
   notice instead of firing 403s at the login host. */

export const getAiConfig = () => {
  if (VENDOR_MODE_ENABLED) return Promise.resolve(notSupportedInVendorMode('getAiConfig'/* NOT_SUPPORTED_IN_VENDOR_MODE */));
  return get(`${BASE}/policies`, undefined, { vendor: false });
};

export const updateAiConfig = (config) => {
  if (VENDOR_MODE_ENABLED) return Promise.resolve(notSupportedInVendorMode('updateAiConfig'/* NOT_SUPPORTED_IN_VENDOR_MODE */));
  return put(`${BASE}/policies`, config, { vendor: false });
};

/* ═══════════════════════════════════════════════════════════════════════════
   Schedule Overrides (internal, local backend only)

   Overrides (blocks, time-off, one-off tweaks) live on the LOCAL backend.
   In vendor-mode deployments the local host is often the same as the
   login/SSO host (e.g. `login.ariaone.ai`), which 403s these paths — so
   we short-circuit with `NOT_SUPPORTED_IN_VENDOR_MODE` instead of routing
   the request through and burning a 403 in the user's face.
   ═══════════════════════════════════════════════════════════════════════════ */

export const listOverrides = (params = {}) => {
  if (VENDOR_MODE_ENABLED) return Promise.resolve(notSupportedInVendorMode('listOverrides'/* NOT_SUPPORTED_IN_VENDOR_MODE */));
  return get(`${BASE}/overrides`, params, { vendor: false });
};

export const createOverride = (override) => {
  requireParam('override.entity_id', override?.entity_id);
  requireParam('override.start', override?.start);
  requireParam('override.end', override?.end);
  if (VENDOR_MODE_ENABLED) return Promise.resolve(notSupportedInVendorMode('createOverride'/* NOT_SUPPORTED_IN_VENDOR_MODE */));
  return post(`${BASE}/overrides`, override, { vendor: false });
};

export const updateOverride = (overrideId, updates) => {
  requireParam('overrideId', overrideId);
  if (VENDOR_MODE_ENABLED) return Promise.resolve(notSupportedInVendorMode('updateOverride'/* NOT_SUPPORTED_IN_VENDOR_MODE */));
  return put(`${BASE}/overrides/${overrideId}`, updates, { vendor: false });
};

export const deleteOverride = (overrideId) => {
  requireParam('overrideId', overrideId);
  if (VENDOR_MODE_ENABLED) return Promise.resolve(notSupportedInVendorMode('deleteOverride'/* NOT_SUPPORTED_IN_VENDOR_MODE */));
  return del(`${BASE}/overrides/${overrideId}`, { vendor: false });
};

/* ═══════════════════════════════════════════════════════════════════════════
   Weekly review — manager-facing narrative digest.
   ═══════════════════════════════════════════════════════════════════════════ */

export const getWeeklyReview = () => get(`${BASE}/weekly-review`);

export const applyWeeklyReview = (body) => post(`${BASE}/weekly-review/apply`, body);

/* ═══════════════════════════════════════════════════════════════════════════
   Internal (non-vendor) capability query — used by the Echo Settings UI.

   Call sites (SchedulerSettings.js) branch on this to render a disabled UI
   state in vendor-mode deployments ("managed per-config in vendor mode")
   rather than firing 403s at the login host. Single source of truth for
   the internal-vs-vendor split.
   ═══════════════════════════════════════════════════════════════════════════ */

/**
 * Returns `true` when the internal non-vendor scheduler helpers
 * (overrides, policies, AI config, checkin/exam/checkout) are safe to
 * invoke in the current runtime. Returns `false` in vendor-mode
 * deployments.
 */
export const isInternalSchedulerSupported = () => !VENDOR_MODE_ENABLED;

/* ═══════════════════════════════════════════════════════════════════════════
   Vendor §Mxx — Rooming / Waitlist / Overbooking / Booking-Widget review
   Published in AriaOne 2026-05-04. All vendor-spec parity (Group A in
   /app/memory/ROADMAP.md). These helpers are PR-ready; backend routes
   are still being implemented — calls will 404 until backend lands.
   ═══════════════════════════════════════════════════════════════════════ */

/* ── Rooming (vendor §M9j) ────────────────────────────────────────────── */
export const getRoomingQueue = (params = {}) => {
  requireParam('params.location_id', params?.location_id);
  return get(`${BASE}/rooming/queue`, params);
};
export const getRoomingRooms = (params = {}) => {
  requireParam('params.location_id', params?.location_id);
  return get(`${BASE}/rooming/rooms`, params);
};
export const getRoomingProviders = (params = {}) => {
  requireParam('params.location_id', params?.location_id);
  return get(`${BASE}/rooming/providers`, params);
};
export const assignRoom = (body) => {
  requireParam('body.appointment_id', body?.appointment_id);
  requireParam('body.room_id', body?.room_id);
  return post(`${BASE}/rooming/assign`, body);
};
export const releaseRoom = (appointmentId, body = {}) => {
  requireParam('appointmentId', appointmentId);
  return post(`${BASE}/rooming/release/${appointmentId}`, body);
};

/* ── Waitlist (vendor §M9k) ───────────────────────────────────────────── */
export const listWaitlist = (params = {}) =>
  get(`${BASE}/waitlist`, params);
export const addToWaitlist = (entry) => {
  requireParam('entry.patient_id', entry?.patient_id);
  return post(`${BASE}/waitlist`, entry);
};
export const updateWaitlistStatus = (waitlistId, body = {}) => {
  requireParam('waitlistId', waitlistId);
  requireParam('body.status', body?.status);
  return patch(`${BASE}/waitlist/${waitlistId}/status`, body);
};
export const removeFromWaitlist = (waitlistId) => {
  requireParam('waitlistId', waitlistId);
  return del(`${BASE}/waitlist/${waitlistId}`);
};
export const getWaitlistAnalytics = (params = {}) =>
  get(`${BASE}/waitlist/analytics`, params);

/* ── Overbooking — A21–A27 (backend shipped 2026-02-feb).
   Seven helpers that wrap the gated capacity-exceeding booking flow.
   Default-safe: when policy.pinned=true and overbook_cap=0 every
   `previewOverbooking` / `overbookAppointment` call returns 409
   (`OVERBOOK_DISABLED`) so callers fall back to the standard path. */
export const getOverbookingPolicy = () =>
  get(`${BASE}/overbooking/policy`);
export const updateOverbookingPolicy = (body) => {
  if (!body || typeof body !== 'object') {
    return Promise.reject(new Error('updateOverbookingPolicy: body required'));
  }
  return put(`${BASE}/overbooking/policy`, body);
};
export const getSlotCapacity = (params = {}) => {
  requireParam('params.entity_id', params?.entity_id);
  requireParam('params.date', params?.date);
  return get(`${BASE}/slots/capacity`, params);
};
export const previewOverbooking = (body) => {
  requireParam('body.entity_id', body?.entity_id);
  requireParam('body.start', body?.start);
  return post(`${BASE}/overbooking/preview`, body);
};
export const overbookAppointment = (body) => {
  requireParam('body.patient_id', body?.patient_id);
  requireParam('body.entity_id', body?.entity_id);
  requireParam('body.start', body?.start);
  requireParam('body.reason', body?.reason);
  return post(`${BASE}/overbooking/book`, body);
};
export const releaseOverbooking = (bookingId, body = {}) => {
  requireParam('bookingId', bookingId);
  return post(`${BASE}/overbooking/${bookingId}/release`, body);
};
export const getOverbookingAudit = (params = {}) =>
  get(`${BASE}/overbooking/audit`, params);

/* ── Vendor §M9l — Legacy contingency-based overbooking helpers.
   Kept for back-compat; not yet wired against a backend. New
   call sites should use the A21–A27 helpers above. */
export const listContingencies = (params = {}) =>
  get(`${BASE}/overbooking/contingencies`, params);
export const createContingency = (body) =>
  post(`${BASE}/overbooking/contingencies`, body);
export const offerContingency = (body) => {
  requireParam('body.contingency_id', body?.contingency_id);
  requireParam('body.patient_id', body?.patient_id);
  return post(`${BASE}/overbooking/offer`, body);
};
export const releaseContingency = (contingencyId, body = {}) => {
  requireParam('contingencyId', contingencyId);
  return post(`${BASE}/overbooking/release/${contingencyId}`, body);
};
export const fillContingency = (contingencyId, params = {}) => {
  requireParam('contingencyId', contingencyId);
  requireParam('params.appointment_id', params?.appointment_id);
  return post(`${BASE}/overbooking/fill/${contingencyId}`, null, { params });
};
export const getOverbookingAnalytics = (params = {}) =>
  get(`${BASE}/overbooking/analytics`, params);
export const getOverbookingCollisions = (params = {}) =>
  get(`${BASE}/overbooking/collisions`, params);

/* ── Vendor policies (replaces the legacy internal-only /policies pair).
   These bypass the previous { vendor: false } pin so the call now hits
   the AriaOne edge per 2026-05-04. The legacy pair (getAiConfig /
   updateAiConfig) is still exported above for back-compat with any
   caller that pre-dates the vendor publication. */
export const getPolicies = () => get(`${BASE}/policies`);
export const updatePolicies = (patchBody) => put(`${BASE}/policies`, patchBody);

/* ── Appointments enriched (vendor §M9 convenience) ───────────────────── */
export const listAppointmentsEnriched = (params = {}) =>
  get(`${BASE}/appointments/enriched`, params);

/**
 * Prefer the enriched payload when the vendor / our backend has provisioned
 * `/appointments/enriched`. Fall back to the basic `/appointments` list in
 * these cases:
 *
 *   (a) Enriched endpoint is genuinely "not deployed" — i.e. status 404
 *       (route missing) or 501 (not implemented).
 *
 *   (b) Enriched endpoint returns 200 OK but with an empty/under-scoped
 *       payload while the caller supplied `entity_id`. Some vendor builds
 *       short-circuit `/appointments/enriched` with `{results: []}` when
 *       they can't satisfy the join (e.g. enriched view depends on a
 *       per-provider index that hasn't backfilled). The basic
 *       `/appointments` endpoint is more permissive and surfaces the
 *       rows. Without this fallback the scheduler grid renders OPEN-only
 *       even though the basic endpoint would have returned bookings.
 *
 * Every other error (401/403 auth, 422 validation, 429 rate-limit, 5xx
 * outages, network status:0) still propagates verbatim so callers see
 * the real failure instead of having it silently masked.
 *
 * The basic and enriched payloads are intentionally normalized to the
 * SAME UI shape by `lib/api/schedulerAdapter.normalizeAppointment` —
 * callers don't need to branch on which shape they receive.
 *
 * Response markers:
 *   • enriched success (non-empty)              → `_enriched: true`
 *   • fallback success on 404/501               → `_enriched: false`, `_enrichedFallback: true`
 *   • fallback success on empty-200             → `_enriched: false`, `_enrichedFallback: true`, `_enrichedFallbackReason: 'empty_200'`
 */
let _enrichedFallbackWarned = false;

// Internal: count of appointment-shaped rows in an arbitrary vendor
// envelope. Keeps the helper free of the schedulerAdapter import.
const _countApptRows = (data) => {
  if (Array.isArray(data)) return data.length;
  if (!data || typeof data !== 'object') return 0;
  for (const key of ['appointments', 'items', 'results', 'records', 'list', 'rows']) {
    if (Array.isArray(data[key])) return data[key].length;
  }
  if (Array.isArray(data.data)) return data.data.length;
  if (data.data && typeof data.data === 'object') {
    for (const key of ['appointments', 'items', 'results']) {
      if (Array.isArray(data.data[key])) return data.data[key].length;
    }
  }
  return 0;
};

export const listAppointmentsPreferEnriched = async (params = {}) => {
  const enriched = await listAppointmentsEnriched(params);

  // 200 OK path — return the enriched payload UNLESS it came back empty
  // while the caller supplied a provider scope. In that case try the
  // basic /appointments endpoint as a one-shot fallback.
  if (enriched?.ok) {
    const rowCount = _countApptRows(enriched.data);
    const hasScope = !!(params && params.entity_id);
    if (rowCount > 0 || !hasScope) {
      return { ...enriched, _enriched: true };
    }
    // Empty enriched response while caller scoped by entity_id —
    // probe the basic endpoint with the same params. Both endpoints
    // empty → return the enriched envelope (preserving _enriched: true)
    // so the UI doesn't think a fallback happened when there's simply
    // no data for this provider/date.
    if (!_enrichedFallbackWarned) {
      _enrichedFallbackWarned = true;
      // eslint-disable-next-line no-console
      console.warn(
        '[Scheduler] /appointments/enriched returned 200 with 0 rows for scoped request — probing /appointments',
        { date: params?.date || null, entity_id: params?.entity_id || null,
          location_id: params?.location_id || null },
      );
    }
    const basic = await listAppointments(params);
    if (basic?.ok && _countApptRows(basic.data) > 0) {
      return {
        ...basic,
        _enriched: false,
        _enrichedFallback: true,
        _enrichedFallbackReason: 'empty_200',
        _enrichedFallbackStatus: 200,
      };
    }
    // Basic also empty (or failed) — return the original enriched 200.
    return { ...enriched, _enriched: true };
  }

  // Non-2xx path — restrict fallback to 404 (route missing) and 501
  // (not implemented). Every other error propagates verbatim — masking
  // 401/403/422/5xx with a fallback hides real failures from the caller.
  const status = enriched?.error?.status ?? null;
  if (!(status === 404 || status === 501)) {
    return enriched;
  }
  const errStatus = status;
  if (!_enrichedFallbackWarned) {
    _enrichedFallbackWarned = true;
    // eslint-disable-next-line no-console
    console.warn(
      '[Scheduler] /appointments/enriched unavailable — falling back to /appointments',
      { status: errStatus, code: enriched?.error?.code || null },
    );
  }
  const basic = await listAppointments(params);
  if (basic?.ok) {
    return {
      ...basic,
      _enriched: false,
      _enrichedFallback: true,
      _enrichedFallbackStatus: errStatus,
    };
  }
  // Both endpoints failed — return the basic error so the UI surfaces
  // its standard availability/load-error banner (instead of silently
  // rendering an empty grid).
  return basic;
};

/* ── Booking widget review (vendor §M9m) ──────────────────────────────── */
export const submitBookingWidgetRequest = (body) =>
  post(`/api/booking/widget/submit`, body);
export const listBookingWidgetReviews = (params = {}) =>
  get(`/api/booking/widget/review`, params);
export const decideBookingWidgetRequest = (requestId, body) => {
  requireParam('requestId', requestId);
  requireParam('body.decision', body?.decision);
  return post(`/api/booking/widget/review/${requestId}/decide`, body);
};



/* ═══════════════════════════════════════════════════════════════════════════
   Back-compat aliases — every external caller still uses these names, but
   they now route through the canonical vendor paths above. DO NOT ADD NEW
   CALLERS — prefer the canonical name on new code. These are kept around
   solely to keep the diff minimal on the UI layer.
   ═══════════════════════════════════════════════════════════════════════════ */

// Legacy aliases → canonical vendor helpers. Each is a thin wrapper that
// simply forwards arguments — it exists purely to avoid churn in UI code
// that was written against the old names. NEW call sites must use the
// canonical name.
export const queryAvailabilityRange = (params) => queryAvailabilityCalendar(params);
export const suggestVisitType = (params) => smartVisitTypes(params);
export const aiBookingApply = (body) => aiApply(body);
